// client/src/pages/HomePage.js
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Alert, Box, Button, Checkbox, Chip, Collapse, Container, FormControl, FormControlLabel, InputLabel, MenuItem,
  Paper, Select, Stack, TextField, Typography,
} from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import TuneIcon from '@mui/icons-material/Tune';
import HomeLanding from '../components/HomeLanding';
import Seo from '../components/Seo';
import { useStore } from '../context/StoreContext';
import ProductSkeleton from '../components/ProductSkeleton';
import ProductCard from '../components/ProductCard';
import EmptyState from '../components/EmptyState';
import { useCategories } from '../context/CategoryContext';
import { BUILD_TYPE_LABELS, getMyModels } from '../utils/productSpecs';
import { getRecentlyViewed } from '../utils/recentlyViewed';
import { API_BASE_URL } from '../utils/media';

const SORT_OPTIONS = [
  { value: '', label: 'Most Relevant' },
  { value: 'newest', label: 'Newest' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'name_asc', label: 'Name: A to Z' },
];

const SPEC_PARAMS = ['buildType', 'powerType', 'scale', 'cells', 'wingspanMin', 'wingspanMax', 'fits'];

const HomePage = () => {
  const { subcategoriesFor } = useCategories();
  const { store } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') || '';
  const category = searchParams.get('category') || '';
  const subcategory = searchParams.get('subcategory') || '';
  const priceMin = searchParams.get('priceMin') || '';
  const priceMax = searchParams.get('priceMax') || '';
  const inStock = searchParams.get('inStock') === 'true';
  const sort = searchParams.get('sort') || '';
  const sale = searchParams.get('sale') === 'true';
  const fits = searchParams.get('fits') || '';
  const specQuery = SPEC_PARAMS.map((key) => [key, searchParams.get(key) || '']).filter(([, value]) => value);
  const specQueryKey = JSON.stringify(specQuery);
  const hasFilters = Boolean(search || category || subcategory || priceMin || priceMax || inStock || sort || sale || specQuery.length > 0);

  const [priceMinInput, setPriceMinInput] = useState(priceMin);
  const [priceMaxInput, setPriceMaxInput] = useState(priceMax);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [facets, setFacets] = useState(null);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentlyViewed] = useState(() => getRecentlyViewed());

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (category) params.set('category', category);
        if (subcategory) params.set('subcategory', subcategory);
        if (priceMin) params.set('priceMin', priceMin);
        if (priceMax) params.set('priceMax', priceMax);
        if (inStock) params.set('inStock', 'true');
        if (sort) params.set('sort', sort);
        if (sale) params.set('sale', 'true');
        JSON.parse(specQueryKey).forEach(([key, value]) => params.set(key, value));
        const response = await axios.get(`${API_BASE_URL}/api/products?${params.toString()}`);
        // A non-array response (e.g. the SPA's own index.html coming back
        // instead of JSON, if a reverse-proxy rule isn't matching /api/...)
        // means something upstream is misconfigured — treat it as a failure
        // instead of silently feeding garbage into the product lists.
        if (!Array.isArray(response.data)) {
          throw new Error('Unexpected non-array response from /api/products');
        }
        setProducts(response.data);
      } catch (err) {
        setError('Failed to fetch products. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [search, category, subcategory, priceMin, priceMax, inStock, sort, sale, specQueryKey]);

  // Which filter choices exist among what's being browsed. Not needed on the homepage.
  useEffect(() => {
    if (!hasFilters) return undefined;
    let cancelled = false;
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category) params.set('category', category);
    if (subcategory) params.set('subcategory', subcategory);
    if (sale) params.set('sale', 'true');
    axios.get(`${API_BASE_URL}/api/products/facets?${params}`)
      .then((response) => { if (!cancelled) setFacets(response.data); })
      .catch(() => { if (!cancelled) setFacets(null); });
    return () => { cancelled = true; };
  }, [hasFilters, search, category, subcategory, sale]);

  // Debounce the price inputs before pushing them into the URL / triggering a fetch.
  useEffect(() => {
    const handle = setTimeout(() => {
      if (priceMinInput === priceMin && priceMaxInput === priceMax) return;
      const next = new URLSearchParams(searchParams);
      if (priceMinInput) next.set('priceMin', priceMinInput); else next.delete('priceMin');
      if (priceMaxInput) next.set('priceMax', priceMaxInput); else next.delete('priceMax');
      setSearchParams(next, { replace: true });
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceMinInput, priceMaxInput]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next);
  };

  if (!hasFilters) {
    const origin = window.location.origin;
    return (
      <>
        <Seo
          title="RC Planes, Helicopters & Model Kits in South Africa"
          description={`Shop RC planes, helicopters, model kits, batteries, radios and parts at ${store.name}. Delivered across South Africa.`}
          canonicalPath="/"
          jsonLd={{
            '@context': 'https://schema.org',
            '@graph': [
              { '@type': 'Organization', name: store.name, url: `${origin}/`, ...(store.support_email ? { email: store.support_email } : {}) },
              {
                '@type': 'WebSite',
                name: store.name,
                url: `${origin}/`,
                potentialAction: { '@type': 'SearchAction', target: `${origin}/?search={search_term_string}`, 'query-input': 'required name=search_term_string' },
              },
            ],
          }}
        />
        <HomeLanding products={products} recentlyViewed={recentlyViewed} loading={loading} error={error} />
      </>
    );
  }

  const location = [category, subcategory].filter(Boolean).join(' / ');
  const title = search
    ? `Results for "${search}"${location ? ` in ${location}` : ''}`
    : location || (fits ? `Parts that fit ${fits}` : sale ? 'Specials' : sort === 'newest' ? 'New Arrivals' : 'All Products');
  const subcategories = category ? subcategoriesFor(category) : [];
  // A category is a page worth finding. A search or a filtered list isn't, and an
  // empty category has nothing to show yet.
  const isCategoryPage = Boolean(category) && !search && !priceMin && !priceMax && !inStock && !sort && !sale && specQuery.length === 0;
  const canonicalQuery = new URLSearchParams();
  if (category) canonicalQuery.set('category', category);
  if (subcategory) canonicalQuery.set('subcategory', subcategory);

  return (
    <Container maxWidth={false} sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, sm: 3, md: 4 } }}>
      <Seo
        title={title}
        description={location ? `Shop ${location} at ${store.name}. Delivered across South Africa.` : undefined}
        canonicalPath={`/?${canonicalQuery}`}
        noindex={!isCategoryPage || (!loading && products.length === 0)}
      />
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" useFlexGap spacing={2} sx={{ mb: subcategories.length > 0 ? 2 : 3 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' }, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.15, overflowWrap: 'anywhere' }}>
            {title}
          </Typography>
          {!loading && !error && (
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              {products.length} {products.length === 1 ? 'product' : 'products'}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<TuneIcon />}
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            sx={{ display: { md: 'none' } }}
          >
            Filters
          </Button>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel id="sort-label">Sort by</InputLabel>
            <Select labelId="sort-label" label="Sort by" value={sort} onChange={(e) => updateParam('sort', e.target.value)}>
              {SORT_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </Stack>

      {subcategories.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{
            mb: 3, flexWrap: { xs: 'nowrap', md: 'wrap' }, overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, md: 0 }, px: { xs: 2, md: 0 },
            scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, '& .MuiChip-root': { flexShrink: 0 },
          }}
        >
          <Chip
            label="All"
            clickable
            color={subcategory ? 'default' : 'primary'}
            variant={subcategory ? 'outlined' : 'filled'}
            onClick={() => updateParam('subcategory', '')}
          />
          {subcategories.map((sub) => (
            <Chip
              key={sub}
              label={sub}
              clickable
              color={subcategory === sub ? 'primary' : 'default'}
              variant={subcategory === sub ? 'filled' : 'outlined'}
              onClick={() => updateParam('subcategory', sub)}
            />
          ))}
        </Stack>
      )}

      <Box sx={{ display: 'grid', gap: 3, alignItems: 'start', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '240px minmax(0, 1fr)' } }}>
        <Box sx={{ position: { md: 'sticky' }, top: { md: 128 } }}>
          {/* Always shown on larger screens; opened with the Filters button on phones. */}
          <Collapse in={filtersOpen} sx={{ display: { md: 'none' } }}>
            <FilterPanel {...{ priceMinInput, setPriceMinInput, priceMaxInput, setPriceMaxInput, inStock, updateParam, facets, searchParams, fits }} />
          </Collapse>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <FilterPanel {...{ priceMinInput, setPriceMinInput, priceMaxInput, setPriceMaxInput, inStock, updateParam, facets, searchParams, fits }} />
          </Box>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {!error && loading && <ProductSkeleton />}
          {!error && !loading && products.length === 0 && (
            <EmptyState
              icon={<SearchOffIcon />}
              title="No products matched your filters"
              description="Try widening your price range or clearing a filter."
              actionLabel="Clear all filters"
              actionTo="/"
            />
          )}
          {!error && !loading && products.length > 0 && (
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(auto-fill, minmax(230px, 1fr))' } }}>
              {products.map((product) => <ProductCard key={product.id} product={product} />)}
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
};

const SpecSelect = ({ label, param, options, searchParams, updateParam }) => {
  const value = searchParams.get(param) || '';
  // Keep a choice from a shared link showing even if it isn't in the current list.
  const choices = value && !options.some((o) => String(o.value) === value) ? [...options, { value, label: value }] : options;
  if (choices.length === 0) return null;
  return (
    <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => updateParam(param, e.target.value)}>
        <MenuItem value="">Any</MenuItem>
        {choices.map((o) => <MenuItem key={o.value} value={String(o.value)}>{o.label}</MenuItem>)}
      </Select>
    </FormControl>
  );
};

const FilterPanel = ({ priceMinInput, setPriceMinInput, priceMaxInput, setPriceMaxInput, inStock, updateParam, facets, searchParams, fits }) => {
  const known = facets?.models || [];
  const mine = getMyModels();
  const modelOptions = [...new Set([...mine, ...known, ...(fits ? [fits] : [])])].map((model) => ({ value: model, label: model }));
  return (
  <Paper sx={{ p: 2.5 }}>
    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2 }}>Filters</Typography>
    <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>Price</Typography>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
      <TextField size="small" type="number" placeholder="Min" aria-label="Minimum price" value={priceMinInput} onChange={(e) => setPriceMinInput(e.target.value)} fullWidth />
      <Typography color="text.secondary">–</Typography>
      <TextField size="small" type="number" placeholder="Max" aria-label="Maximum price" value={priceMaxInput} onChange={(e) => setPriceMaxInput(e.target.value)} fullWidth />
    </Stack>
    <FormControlLabel
      control={<Checkbox checked={inStock} onChange={(e) => updateParam('inStock', e.target.checked ? 'true' : '')} />}
      label="In stock only"
      sx={{ display: 'block', mb: 1.5 }}
    />
    <SpecSelect label="Fits my model" param="fits" options={modelOptions} {...{ searchParams, updateParam }} />
    <SpecSelect label="Type" param="buildType" options={(facets?.buildTypes || []).map((v) => ({ value: v, label: BUILD_TYPE_LABELS[v] || v }))} {...{ searchParams, updateParam }} />
    <SpecSelect label="Power" param="powerType" options={(facets?.powerTypes || []).map((v) => ({ value: v, label: v }))} {...{ searchParams, updateParam }} />
    <SpecSelect label="Scale" param="scale" options={(facets?.scales || []).map((v) => ({ value: v, label: v }))} {...{ searchParams, updateParam }} />
    <SpecSelect label="Battery cells" param="cells" options={(facets?.cells || []).map((v) => ({ value: v, label: `${v}S` }))} {...{ searchParams, updateParam }} />
    {facets?.hasWingspan && (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>Wingspan (mm)</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            key={`min-${searchParams.get('wingspanMin') || ''}`} size="small" type="number" placeholder="Min" aria-label="Minimum wingspan"
            defaultValue={searchParams.get('wingspanMin') || ''} onBlur={(e) => updateParam('wingspanMin', e.target.value)} fullWidth
          />
          <Typography color="text.secondary">–</Typography>
          <TextField
            key={`max-${searchParams.get('wingspanMax') || ''}`} size="small" type="number" placeholder="Max" aria-label="Maximum wingspan"
            defaultValue={searchParams.get('wingspanMax') || ''} onBlur={(e) => updateParam('wingspanMax', e.target.value)} fullWidth
          />
        </Stack>
      </Box>
    )}
    <Button component={Link} to="/" size="small" variant="outlined" fullWidth>Clear all filters</Button>
  </Paper>
  );
};

export default HomePage;
